import { memo, useMemo } from "react";
import {
  EuiTableRowCell,
  EuiPopover,
  EuiContextMenu,
} from "@inscopix/ideas-eui";
import { useProjectTablesTableContext } from "components/ProjectTablesTable/provider/ProjectTablesTableProvider";
import { useProjectFolderContext } from "pages/gdt/folders/ProjectFoldersProvider";
import {
  ProjectTablesTableColumn,
  ProjectDataTable,
} from "components/ProjectTablesTable/ProjectTablesTable.types";
import { ButtonIconPermissioned } from "components/ButtonIconPermissioned/ButtonIconPermissioned";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";

type DataTablesTableActionMenuProps = {
  column: ProjectTablesTableColumn;
  table: ProjectDataTable;
};

/*
 * Action menu popover for individual rows of the project tables table
 * Allows for:
 * - Invoking table edit modal
 * - Moving table to or out of folder
 * - Invoking table delete modal
 */
export const ProjectTablesTableActionMenu = memo(
  function ProjectTablesTableActionMenu({
    column,
    table,
  }: DataTablesTableActionMenuProps) {
    const folders = useProjectFolderContext((s) => s.getSortedFolders());
    const togglePopover = useProjectTablesTableContext((s) => s.togglePopover);
    const popoverId = useProjectTablesTableContext((s) => s.popoverId);
    const closePopover = useProjectTablesTableContext((s) => s.closePopover);
    const toggleTableHidden = useProjectTablesTableContext(
      (s) => s.toggleTableHidden,
    );
    const openEditTableModal = useProjectTablesTableContext(
      (s) => s.openEditTableModal,
    );
    const openDeleteTableModal = useProjectTablesTableContext(
      (s) => s.openDeleteTableModal,
    );
    const moveTableToFolder = useProjectTablesTableContext(
      (s) => s.moveTableToFolder,
    );

    // avoiding EuiIcon heavy rerenders
    const button = useMemo(
      () => (
        <ButtonIconPermissioned
          aria-label="Table Actions"
          data-test-subj="table-actions-button"
          iconType="gear"
          size="s"
          color="text"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            togglePopover(table.id);
          }}
          requiredPermission="edit"
        />
      ),
      [table.id, togglePopover],
    );

    const handleFolderMove = (folderId: string | null) => {
      moveTableToFolder(table, folderId);
      closePopover();
    };

    const handleTableShowHide = async (hidden: boolean) => {
      const { error } = await toggleTableHidden(table, hidden);
      if (error === undefined) {
        addUtilityToastSuccess("Table updated");
      } else {
        addUtilityToastFailure("Failed to update table");
      }
      closePopover();
    };

    const folderItems = [...folders]
      .filter((folder) => folder.id !== table.folder)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((folder) => ({
        name: folder.name,
        icon: "folderOpen",
        onClick: () => {
          handleFolderMove(folder.id);
        },
      }));

    const contextFolderItems =
      table.folder !== null
        ? [
            {
              name: "Remove from folder",
              icon: "returnKey",
              onClick: () => {
                handleFolderMove(null);
              },
            },
            ...folderItems,
          ]
        : folderItems;

    const contextPanels = [
      {
        id: 0,
        width: 120,
        items: [
          {
            name: "Edit",
            icon: "pencil",
            onClick: (e: React.MouseEvent<Element>) => {
              openEditTableModal(table);
              closePopover();
            },
          },
          {
            name: "Move",
            icon: "folderOpen",
            panel: 1,
          },
          {
            name: table.hidden ? "Show" : "Hide",
            icon: table.hidden ? "eye" : "eyeClosed",
            onClick: (e: React.MouseEvent<Element>) => {
              closePopover();
              void handleTableShowHide(!table.hidden);
            },
          },
          {
            name: "Delete",
            icon: "trash",
            onClick: (e: React.MouseEvent<Element>) => {
              closePopover();
              openDeleteTableModal(table);
            },
          },
        ],
      },
      {
        id: 1,
        title: "Move to folder",
        items: contextFolderItems,
      },
    ];

    return (
      <EuiTableRowCell
        key={column.id}
        textOnly={false}
        hasActions={true}
        align="right"
      >
        <EuiPopover
          id={`${table.id}-actions`}
          button={button}
          isOpen={table.id === popoverId}
          closePopover={() => closePopover()}
          panelPaddingSize="none"
          anchorPosition="leftCenter"
        >
          <EuiContextMenu initialPanelId={0} panels={contextPanels} />
        </EuiPopover>
      </EuiTableRowCell>
    );
  },
);
